"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Layout } from "./Layout"
import { useAuth } from "../hooks/useAuth"
import { db } from "../firebase"
import { ref, onValue, update } from "../lib/firebase-database"
import { useQueryCount } from "../hooks/useQueryCount"
import { Button } from "./ui/button"
import { Check } from "lucide-react"

export default function UsersQueryPage() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [queries, setQueries] = useState([])
  const { markQueriesAsViewed } = useQueryCount()
  const [readQueries, setReadQueries] = useState(new Set())

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/")
      return
    }

    const queriesRef = ref(db, "userQueries")
    const unsubscribe = onValue(queriesRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const queryList = Object.entries(data)
          .map(([id, query]) => ({
            id,
            ...query,
            read: query.read || false
          }))
          .sort((a, b) => b.timestamp - a.timestamp)
        setQueries(queryList)
        
        // Initialize read states and mark initially read queries
        const readQueryIds = queryList.filter(q => q.read).map(q => q.id)
        setReadQueries(new Set(readQueryIds))
        markQueriesAsViewed(readQueryIds) // Mark initially read queries in global state
      } else {
        setQueries([])
      }
    })

    return () => unsubscribe()
  }, [user, isAdmin, navigate, markQueriesAsViewed])

  const handleMarkAsRead = async (queryId) => {
    try {
      const queryRef = ref(db, `userQueries/${queryId}`)
      await update(queryRef, { 
        read: true,
        readAt: Date.now(),
        readBy: user.uid
      })
      
      // Update local state
      setReadQueries(prev => new Set([...prev, queryId]))
      
      // Update global state and remove from unread queries
      markQueriesAsViewed([queryId])
      
      // Also update the queries state to reflect the change
      setQueries(prev => 
        prev.map(query => 
          query.id === queryId 
            ? { ...query, read: true, readAt: Date.now(), readBy: user.uid }
            : query
        )
      )
    } catch (error) {
      console.error("Error marking query as read:", error)
    }
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString()
  }

  return (
    <Layout>
      <div className="space-y-6 p-4">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">User Queries</h1>
          <p className="text-muted-foreground">View and manage user inquiries</p>
        </div>

        {queries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No queries received yet</div>
        ) : (
          <div className="grid gap-4">
            {queries.map((query) => (
              <div 
                key={query.id} 
                className={`bg-white rounded-lg shadow-md p-6 relative border-l-4 transition-colors
                  ${query.read || readQueries.has(query.id) 
                    ? 'border-green-500' 
                    : 'border-yellow-500 animate-pulse'}`}
              >
                <div className="grid gap-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div>
                        <span className="font-medium">Name:</span> {query.name}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {query.phone}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {query.email || "Not provided"}
                      </div>
                      <div>
                        <span className="font-medium">Subject:</span> {query.subject || "No subject"}
                      </div>
                      <div>
                        <span className="font-medium">Message:</span> {query.message}
                      </div>
                      <div className="text-sm text-gray-500">
                        <span className="font-medium">Received:</span> {formatDate(query.timestamp)}
                      </div>
                    </div>
                    
                    {!readQueries.has(query.id) && (
                      <Button
                        onClick={() => handleMarkAsRead(query.id)}
                        className="bg-green-500 hover:bg-green-600 text-white ml-4"
                        size="sm"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark as Read
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}